import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import BackButton from "../components/BackButton";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import { fetchActowizProducts } from "../store/actowiz/actowizSlice";
import { apiRequest } from "../api/client";
import { useToast } from "../components/ToastProvider";

const PLATFORM_LABELS = {
  tesco: "Tesco",
  asda: "ASDA",
  morrison: "Morrisons",
  sainsbury: "Sainsbury's",
};

const parseMoney = (value) => {
  if (value == null) return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  const cleaned = String(value).trim().replaceAll(",", ".").replace(/[^0-9.-]/g, "");
  if (!cleaned) return null;
  const parsed = Number.parseFloat(cleaned);
  return Number.isFinite(parsed) ? parsed : null;
};

const parseRatingValue = (value) => {
  if (value == null) return null;
  const parsed = Number.parseFloat(String(value).trim().replace(",", ".").replace(/[^0-9.]/g, ""));
  return Number.isFinite(parsed) ? parsed : null;
};

const parseReviewCountValue = (value) => {
  if (value == null) return null;
  const parsed = Number.parseInt(String(value).replace(/[^0-9]/g, ""), 10);
  return Number.isFinite(parsed) ? parsed : null;
};

const getProductImages = (prod) => {
  if (!prod) return [];
  if (Array.isArray(prod.images) && prod.images.length) {
    return prod.images.filter(Boolean);
  }
  const fallback = [
    prod.image,
    prod.image_url,
    prod.imageUrl,
    prod.thumbnail,
    prod.thumb,
  ].filter(Boolean);
  return fallback;
};

function ProductDetailSingle({ onOpenAuth, onOpenMenu }) {
  const { platform, id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const byPlatform = useAppSelector((s) => s.actowiz.byPlatform);
  const statusByPlatform = useAppSelector((s) => s.actowiz.statusByPlatform);
  const errorByPlatform = useAppSelector((s) => s.actowiz.errorByPlatform);

  const p = String(platform || "").trim().toLowerCase();
  const platformLabel = PLATFORM_LABELS[p] || "Retailer";

  const [didKickoffFetch, setDidKickoffFetch] = useState(false);
  const [addBusy, setAddBusy] = useState(false);
  const [listPickerOpen, setListPickerOpen] = useState(false);
  const [listPickerOptions, setListPickerOptions] = useState([]);
  const [listPickerResolver, setListPickerResolver] = useState(null);
  const { showToast } = useToast();

  const closeListPicker = useCallback(() => {
    listPickerResolver?.(null);
    setListPickerOpen(false);
    setListPickerOptions([]);
    setListPickerResolver(null);
  }, [listPickerResolver]);

  const chooseList = useCallback((lists) => {
    return new Promise((resolve) => {
      setListPickerOptions(lists);
      setListPickerResolver(() => resolve);
      setListPickerOpen(true);
    });
  }, []);

  const matchesId = (prod, matchId) => {
    if (!prod || matchId == null) return false;
    const candidateIds = [
      prod?.pid,
      prod?.product_id,
      prod?.sku,
      prod?.id,
      prod?.productId,
      prod?.productID,
    ]
      .map((v) => (v == null ? "" : String(v)))
      .filter(Boolean);
    return candidateIds.includes(String(matchId));
  };

  const navigatedProduct = useMemo(() => {
    const candidate = location?.state?.product;
    if (!candidate || !id) return null;
    return matchesId(candidate, id) ? candidate : null;
  }, [location?.state, id]);

  useEffect(() => {
    if (!p || !id) return;
    if (navigatedProduct) return;
    if (didKickoffFetch) return;
    const list = byPlatform?.[p] || [];
    const alreadyHas = Array.isArray(list) && list.some((x) => matchesId(x, id));
    if (alreadyHas) return;

    // Fetch from products API (no product_detail call).
    setDidKickoffFetch(true);
    dispatch(
      fetchActowizProducts({
        platform: p,
        start: 0,
        offset: 60,
        mode: "replace",
        force: true,
      })
    );
  }, [dispatch, p, id, byPlatform, didKickoffFetch, navigatedProduct]);

  const product = useMemo(() => {
    if (navigatedProduct) return navigatedProduct;
    const list = byPlatform?.[p] || [];
    if (!Array.isArray(list) || !id) return null;
    return list.find((x) => matchesId(x, id)) || null;
  }, [id, p, byPlatform, navigatedProduct]);

  const images = useMemo(() => getProductImages(product), [product]);
  const [selectedImage, setSelectedImage] = useState("");
  useEffect(() => {
    setSelectedImage(images[0] || "");
  }, [id, images]);

  const numericPrice = useMemo(
    () =>
      parseMoney(
        product?.price ??
          product?.unit_price ??
          product?.price_per_unit ??
          product?.pricePerUnit ??
          product?.offer_price ??
          product?.offerPrice
      ),
    [product]
  );

  const hasValidPrice = numericPrice != null;

  const priceLabel = useMemo(() => {
    if (numericPrice != null) return `£${numericPrice.toFixed(2)}`;
    return "£—";
  }, [numericPrice]);
  const ratingValue = useMemo(
    () => parseRatingValue(product?.average_rating ?? product?.avg_rating ?? product?.rating),
    [product]
  );
  const reviewCountValue = useMemo(
    () =>
      parseReviewCountValue(
        product?.review_count ?? product?.reviews ?? product?.reviewCount ?? product?.rating_count
      ),
    [product]
  );

  const sizeOptions = useMemo(() => {
    const raw = String(product?.packsize || "").trim();
    const list = raw
      ? raw
          .split(/[,/|]/g)
          .map((s) => s.trim())
          .filter(Boolean)
      : [];
    return list.length ? list.slice(0, 6) : ["—"];
  }, [product?.packsize]);

  const [selectedSize, setSelectedSize] = useState("");
  useEffect(() => {
    if (!sizeOptions.length) return;
    setSelectedSize((prev) => (prev && sizeOptions.includes(prev) ? prev : sizeOptions[0] || ""));
  }, [id, sizeOptions]);

  const status = statusByPlatform?.[p] || "idle";
  const error = errorByPlatform?.[p] || "";

  async function handleAddToList() {
    try {
      setAddBusy(true);
      const minimalRes = await apiRequest("api/grocery_lists_minimal", { method: "GET" });
      if (!minimalRes.ok) {
        const errors = Array.isArray(minimalRes.data?.errors) ? minimalRes.data.errors : null;
        throw new Error(errors?.join("\n") || minimalRes.data?.message || "Failed to load grocery lists.");
      }

      const lists = Array.isArray(minimalRes.data)
        ? minimalRes.data
        : Array.isArray(minimalRes.data?.data)
        ? minimalRes.data.data
        : [];

      if (!lists.length) {
        showToast({
          type: "error",
          title: "No grocery list",
          message: "No grocery list found. Please create a list first.",
          actionLabel: "Create list",
          onAction: () => navigate("/settings?tab=grocery&view=create"),
        });
        return;
      }

      let selectedListId = null;
      if (lists.length === 1) {
        selectedListId = lists[0]?.id ?? lists[0]?.grocery_list_id ?? lists[0]?.list_id ?? null;
      } else {
        const selected = await chooseList(lists);
        if (!selected) return;
        selectedListId = selected?.id ?? selected?.grocery_list_id ?? selected?.list_id ?? null;
      }
      if (!selectedListId) throw new Error("Unable to select grocery list.");

      const productId = product?.product_id ?? product?.pid ?? product?.sku ?? product?.id ?? id ?? null;
      if (!productId) throw new Error("Invalid product.");

      const addRes = await apiRequest(`api/grocery_lists/${selectedListId}/grocery_products`, {
        method: "POST",
        body: {
          product_id: String(productId),
          product_name: product?.title || product?.product_name || product?.name || product?.sku || "",
          category: product?.category || product?.category_name || undefined,
          subcategory: product?.subcategory || product?.sub_category || product?.subCategory || undefined,
          platform: p || undefined,
          image_url: images[0] || "",
          price: numericPrice ?? null,
          weight: selectedSize || product?.packsize || product?.weight || "",
          quantity: 1,
        },
      });
      if (!addRes.ok) {
        const errors = Array.isArray(addRes.data?.errors) ? addRes.data.errors : null;
        throw new Error(errors?.join("\n") || addRes.data?.message || "Failed to add product to list.");
      }

      showToast({
        type: "success",
        title: "Added to list",
        message: "Product added to grocery list.",
        actionLabel: "Go to Grocery List",
        onAction: () => navigate("/settings?tab=grocery&view=lists"),
      });
    } catch (err) {
      showToast({
        type: "error",
        title: "Add failed",
        message: err?.message || "Failed to add product to list.",
      });
    } finally {
      setAddBusy(false);
    }
  }

  return (
    <div className="min-h-screen bg-[#F5F7FB] text-[#24303A] flex flex-col">
      <Header onOpenAuth={onOpenAuth} onOpenMenu={onOpenMenu} />

      <main className=" w-full  flex-1 px-4 py-8 md:px-6">
        <div className="mb-6 rounded-2xl border border-[#E6EAF0] bg-white p-5 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <BackButton />
            <button
              type="button"
              onClick={() => navigate(`/platform/${encodeURIComponent(p)}`)}
              className="rounded-full bg-[#7352C7] px-4 py-2 text-[16px] font-extrabold text-white shadow-sm hover:bg-[#6546c3]"
            >
              View all {platformLabel}
            </button>
          </div>
        </div>

        {status === "loading" && (
          <div className="rounded-2xl border border-[#E6EAF0] bg-white p-5 text-center text-[16px] text-[#6E7B86] shadow-sm">
            Loading product details…
          </div>
        )}

        {status === "failed" && (
          <div className="rounded-2xl border border-red-200 bg-red-50 p-5 text-center text-[16px] text-red-700 shadow-sm">
            {error || "Failed to load product details."}
          </div>
        )}

        {status !== "loading" && !product && !error && (
          <div className="rounded-2xl border border-[#E6EAF0] bg-white p-5 text-center text-[16px] text-[#6E7B86] shadow-sm">
            {didKickoffFetch ? "Product not found." : "Loading…"}
          </div>
        )}

        {product && (
          <div className="grid gap-8 md:grid-cols-[500px_minmax(0,1fr)]">
            <div className="space-y-4">
              <div className="flex items-center justify-center rounded-2xl border border-[#E6EAF0] bg-white p-4 shadow-sm">
                {selectedImage ? (
                  <img
                    src={selectedImage}
                    alt={product?.title || product?.sku}
                    className="max-h-72 object-contain"
                  />
                ) : (
                  <div className="flex h-72 w-full items-center justify-center rounded-xl bg-[#F5F7FB] text-[16px] text-[#6E7B86]">
                    No Image
                  </div>
                )}
              </div>

              {images.length > 1 && (
                <div className="flex gap-8 overflow-x-auto p-3">
                  {images.map((src, idx) => {
                    const isActive = src === selectedImage;
                    return (
                      <button
                        key={`${src}-${idx}`}
                        type="button"
                        onClick={() => setSelectedImage(src)}
                        className={[
                          "flex h-24 w-24 shrink-0 items-center justify-center overflow-hidden rounded-xl border bg-white",
                          isActive ? "border-[#7352C7] ring-2 ring-[#7352C7]/20" : "border-[#E6EAF0]",
                        ].join(" ")}
                        aria-label={`View image ${idx + 1}`}
                        aria-pressed={isActive}
                      >
                        <img
                          src={src}
                          alt={`${product?.title || product?.sku || "Product"} thumbnail ${idx + 1}`}
                          className="h-full w-full object-contain bg-white p-4"
                          loading="lazy"
                        />
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="space-y-6 rounded-3xl border border-[#E6EAF0] bg-white p-6 shadow-sm">
              <div className="grid grid-cols-2 items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="inline-flex items-center gap-2 rounded-full bg-[#7352C7]/10 px-3 py-1 text-[16px] font-bold text-[#7352C7]">
                    {platformLabel}
                  </div>
                  <h1 className="mt-1 text-[28px] font-bold text-[#24303A]">
                    {product?.title || product?.sku || "N/A"}
                  </h1>
                  {(Number.isFinite(ratingValue) || Number.isFinite(reviewCountValue)) && (
                    <div className="mt-1 inline-flex items-center rounded-full bg-[#7352C7]/10 px-3 py-1.5  justify-center gap-1 leading-none text-[16px] font-bold text-[#7352C7]">
                      {Number.isFinite(ratingValue) && (
                        <>
                          <StarRating rating={ratingValue} />
                          <span className="leading-none">{ratingValue.toFixed(1)}</span>
                        </>
                      )}
                      |
                      {Number.isFinite(reviewCountValue) && (
                        <span className="leading-none">({reviewCountValue} Reviews)</span>
                      )}
                    </div>
                  )}
                </div>

                <div className="rounded-2xl ml-28 bg-emerald-50 px-4 py-3 text-right text-[16px] border border-emerald-100">
                  <p className="text-[16px] font-normal text-[#2FC98D]">Price</p>
                  <p className="text-[28px] font-bold text-[#2FC98D]">{priceLabel}</p>
                  {(Number.isFinite(ratingValue) || Number.isFinite(reviewCountValue)) && (
                    <div className="mt-1 inline-flex items-center justify-center gap-1 leading-none text-[16px] font-semibold text-[#2FC98D]">
                      {Number.isFinite(ratingValue) && (
                        <>
                          <StarRating rating={ratingValue} />
                          <span className="leading-none">{ratingValue.toFixed(1)}</span>
                        </>
                      )}
                      {Number.isFinite(reviewCountValue) && (
                        <span className="leading-none">({reviewCountValue} Reviews)</span>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <section className="space-y-2 text-[16px]">
                <h2 className="text-[20px] font-semibold text-[#24303A]">Good to know</h2>
                <p className="text-[16px] text-[#6E7B86] font-normal whitespace-pre-line">
                  {product?.description
                    ? product.description
                    : "Product description is not available for this item yet."}
                </p>
              </section>

              {product?.url ? (
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    className="inline-flex items-center justify-center rounded-full bg-[#7352C7] px-5 py-2 text-[18px] font-medium text-white disabled:cursor-not-allowed disabled:opacity-60"
                    onClick={handleAddToList}
                    disabled={addBusy || !hasValidPrice}
                  >
                    {addBusy ? "Adding..." : "Add to List"}
                  </button>
                  {hasValidPrice ? (
                    <a
                      href={product.url}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center justify-center rounded-full bg-[#7352C71A] px-4 py-2 text-[18px] font-medium text-[#7352C7]"
                    >
                      Visit Store
                    </a>
                  ) : (
                    <button
                      type="button"
                      disabled
                      className="inline-flex cursor-not-allowed items-center justify-center rounded-full bg-slate-100 px-4 py-2 text-[18px] font-medium text-slate-400"
                    >
                      Visit Store
                    </button>
                  )}
                </div>
              ) : null}
            </div>
          </div>
        )}
      </main>

      <Footer />

      {listPickerOpen && (
        <div className="fixed inset-0 z-[80]">
          <button
            type="button"
            className="absolute inset-0 bg-black/40"
            aria-label="Close list selector"
            onClick={closeListPicker}
          />
          <div className="relative mx-auto flex min-h-screen max-w-3xl items-center justify-center px-4 py-8">
            <div className="w-full max-w-md rounded-2xl border border-[#E6EAF0] bg-white p-5 shadow-xl">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="text-base font-bold text-[#24303A]">Choose Grocery List</h3>
                  <p className="mt-1 text-[16px] text-slate-500">Select the list where you want to add this product.</p>
                </div>
                <button
                  type="button"
                  onClick={closeListPicker}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-[#E6EAF0] bg-white text-slate-600 hover:bg-slate-50"
                  aria-label="Close popup"
                >
                  ✕
                </button>
              </div>
              <div className="mt-4 space-y-2">
                {listPickerOptions.map((list, index) => {
                  const listId = list?.id ?? list?.grocery_list_id ?? list?.list_id ?? "";
                  const listTitle = list?.title || `List ${index + 1}`;
                  return (
                    <button
                      key={`${listId}-${index}`}
                      type="button"
                      className="w-full rounded-xl border border-[#E6EAF0] bg-white px-3 py-2 text-left hover:bg-slate-50"
                      onClick={() => {
                        listPickerResolver?.(list);
                        setListPickerOpen(false);
                        setListPickerOptions([]);
                        setListPickerResolver(null);
                      }}
                    >
                      <p className="text-[16px] font-semibold text-slate-800">{listTitle}</p>
                      <p className="text-[11px] text-slate-500">ID: {String(listId || "-")}</p>
                    </button>
                  );
                })}
              </div>
              <div className="mt-4 flex justify-end">
                <button
                  type="button"
                  onClick={closeListPicker}
                  className="rounded-lg border border-[#E6EAF0] bg-white px-3 py-1.5 text-[16px] font-semibold text-slate-700 hover:bg-slate-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ProductDetailSingle;

function StarRating({ rating }) {
  const clamped = Math.max(0, Math.min(5, rating || 0));
  return (
    <div
      className="inline-flex items-center gap-0.5 leading-none"
      aria-label={`Rating ${clamped.toFixed(1)} out of 5`}
    >
      {[0, 1, 2, 3, 4].map((index) => {
        const fillPercent = Math.max(0, Math.min(1, clamped - index));
        return (
          <span key={index} className="relative inline-flex h-3.5 w-3.5 items-center justify-center leading-none">
            <span className="absolute inset-0 flex items-center justify-center leading-none text-slate-300">
              ★
            </span>
            <span
              className="absolute inset-0 flex items-center justify-start overflow-hidden leading-none text-amber-400"
              style={{ width: `${fillPercent * 100}%` }}
            >
              ★
            </span>
          </span>
        );
      })}
    </div>
  );
}

